<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { getLottieData } from '@/icons/lottie';
import { pxOrValue, hasSlotContent } from '../../utils';
interface Props {
  color?: string;
  size?: number | string;
}
const props = withDefaults(defineProps<Props>(), {
  color: undefined,
  size: undefined,
});
const cssVariables = computed(() => ({
  '--spinner-size': pxOrValue(props.size),
}));
const lottieContainer = ref<HTMLElement | null>(null);
type LottieModule = {
  loadAnimation: (config: Record<string, unknown>) => {
    destroy: () => void;
  };
};
let lottieModule: LottieModule | null = null;
let lottieAnimation: {
  destroy: () => void;
} | null = null;
function resolveColor(rawColor?: string): string | undefined {
  const fallback = 'rgba(137, 98, 146, 1)';
  const candidate = rawColor?.trim() || `var(--ui2-primitive-lavender-mist-450, ${fallback})`;
  const match = candidate.match(/var\(\s*(--[^,\s)]+)\s*(?:,\s*([^)]+))?\)/i);
  if (!match || typeof window === 'undefined') {
    return candidate;
  }
  const resolved = getComputedStyle(document.documentElement).getPropertyValue(match[1]).trim();
  return resolved || match[2]?.trim() || candidate;
}
async function loadLottieModule() {
  if (lottieModule) {
    return lottieModule;
  }
  const module = await import('lottie-web');
  const normalizedModule = module as unknown as {
    default?: LottieModule;
  };
  lottieModule = normalizedModule.default ?? (module as unknown as LottieModule);
  return lottieModule;
}
function destroyLottie() {
  if (lottieAnimation) {
    lottieAnimation.destroy();
    lottieAnimation = null;
  }
  if (lottieContainer.value) {
    lottieContainer.value.innerHTML = '';
  }
}
async function renderLottie() {
  if (!lottieContainer.value) {
    return;
  }
  const data = await getLottieData('loader', resolveColor(props.color));
  if (!data) {
    destroyLottie();
    return;
  }
  destroyLottie();
  const { loadAnimation } = await loadLottieModule();
  lottieAnimation = loadAnimation({
    container: lottieContainer.value,
    renderer: 'svg',
    loop: true,
    autoplay: true,
    animationData: data as Record<string, unknown>,
  });
}
watch(
  () => props.color,
  async () => {
    await nextTick();
    await renderLottie();
  },
  { immediate: true },
);
onBeforeUnmount(() => {
  destroyLottie();
});
</script>

<template>
  <div role="progressbar" :class="$style.root" :style="cssVariables">
    <div :class="$style.icon">
      <span ref="lottieContainer" />
    </div>
    <div v-if="hasSlotContent($slots.default)" :class="$style.text">
      <slot />
    </div>
  </div>
</template>

<style lang="scss" module>
@use '../../styles/colors';
@use '../../styles/iconography';

.root {
  display: inline-flex;
  vertical-align: top;
  align-items: center;
  --spinner-size: 24px;
}
.icon {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: iconography.$icon-size-m;
  width: var(--spinner-size);
  height: var(--spinner-size);

  > svg {
    width: 100%;
    height: 100%;
  }

  > span {
    width: 100%;
    height: 100%;
  }
}

.text {
  font-size: 16px;
  line-height: 24px;
  margin-left: 12px;
  color: colors.$primary;
}
</style>
